<template>
  <b-card>
    <b-row>
      <b-col cols="12">
        <h2 class="text-blue font-weight-bold mt-1">
          {{ $t("action_logs") }}
        </h2>
      </b-col>
    </b-row>
    <b-row class="action-logs-filters mb-3 align-items-end">
      <b-col cols="12" sm="6" md="2" >
        <b-form-group
          :label="$t('start_date') + ':'"
          label-for="action-logs-start-date"
          class="mb-0"
        >
          <b-form-input
            id="action-logs-start-date"
            v-model.trim="searchData.start_date"
            type="date"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" sm="6" md="2">
        <b-form-group
          :label="$t('end_date') + ':'"
          label-for="action-logs-end-date"
          class="mb-0"
        >
          <b-form-input
            id="action-logs-end-date"
            v-model.trim="searchData.end_date"
            type="date"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" sm="6" md="6" lg="3">
        <b-form-group
          :label="$t('action') + ':'"
          label-for="action-logs-action"
          class="mb-0"
        >
          <b-form-select
            id="action-logs-action"
            v-model="selectedAction"
            :options="actionOptions"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" sm="6" md="6" lg="3">
        <b-form-group
          :label="$t('user') + ':'"
          label-for="action-logs-user"
          class="mb-0"
        >
          <b-form-input
            id="action-logs-user"
            v-model.trim="userFilter"
            type="search"
            :placeholder="$t('type_to_search')"
            @keyup.enter="applyFilters"
          />
        </b-form-group>
      </b-col>
      <b-col cols="12" lg="auto" class="d-flex align-items-end flex-wrap mb-2 mb-lg-0 action-logs-filters__actions">
        <b-button variant="secondary" class="mr-1 mb-2 mb-lg-0" @click="applyFilters">
          {{ $t('search') }}
        </b-button>
        <b-button variant="outline-secondary" class="mb-2 mb-lg-0" @click="clearFilters">
          {{ $t('clear') }}
        </b-button>
      </b-col>
    </b-row>
    <Table
      large
      :fields="fields"
      url="/action-logs"
      :show-search="false"
      :body="filterBody"
      :update-data="updateData"
      responsive="sm"
    >
      <template #cell(user_id)="data">
        <span v-if="data.item.user">
          {{ formatLogUserName(data.item.user) }}
        </span>
        <span v-else-if="data.item.user_id">—</span>
        <span v-else>{{ $t('action_log_system') }}</span>
      </template>
      <template #cell(action)="data">
        {{$t(data.item.action) }}
      </template>
      <template #cell(entity_type)="data">
        {{$t(data.item.entity_type) }}
      </template>
      <template #cell(action_context)="data">
        <span
          v-if="hasContext(data.item.context)"
          class="text-truncate d-inline-block"
          style="max-width: 320px;"
          :title="formatContextTitle(data.item.context)"
        >
          {{ formatContextDisplay(data.item.context) }}
        </span>
        <span v-else>—</span>
      </template>
      <template #cell(created_at)="data">
        <span v-if="data.item.created_at">
          {{ data.item.created_at | formatDate }}
        </span>
        <span v-else>—</span>
      </template>
    </Table>
  </b-card>
</template>

<script>
import { BCard, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BButton } from 'bootstrap-vue'
import Table from '@/components/Common/Table2.vue'

export default {
  name: 'ActionLogs',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    Table,
  },
  data() {
    return {
      updateData: 0,
      selectedAction: '',
      userFilter: '',
      actionTypes: [],
      searchData: {
        start_date: '',
        end_date: '',
      },
      fields: [
        { key: 'created_at', label: 'time', sortable: true },
        { key: 'user_id', label: 'user', sortable: true },
        { key: 'action', label: 'action', sortable: true },
        { key: 'entity_type', label: 'entity_type', sortable: true },
        { key: 'entity_id', label: 'entity_id', sortable: true },
        { key: 'action_context', label: 'log_context', sortable: false },
      ],
    }
  },
  computed: {
    actionOptions() {
      return [
        { value: '', text: this.$t('all') },
        ...this.actionTypes.map((action) => ({
          value: action,
          text: this.$te(action) ? this.$t(action) : action,
        })),
      ]
    },
    filterBody() {
      const body = {}
      if (this.searchData.start_date) {
        body.start_date = this.searchData.start_date
      }
      if (this.searchData.end_date) {
        body.end_date = this.searchData.end_date
      }
      if (this.selectedAction) {
        body.action_type = this.selectedAction
      }
      if (this.userFilter) {
        body.user = this.userFilter
      }
      return body
    },
  },
  async created() {
    await this.loadFilterOptions()
  },
  methods: {
    formatLogUserName(user) {
      const name = [user.name, user.last_name].filter(Boolean).join(' ').trim()
      return name || '—'
    },
    async loadFilterOptions() {
      try {
        const actionsRes = await this.$http.get('/action-logs/action-types')
        this.actionTypes = actionsRes?.data?.value || []
      } catch (e) {
        this.actionTypes = []
      }
    },
    applyFilters() {
      this.updateData += 1
    },
    clearFilters() {
      this.selectedAction = ''
      this.userFilter = ''
      this.searchData.start_date = ''
      this.searchData.end_date = ''
      this.updateData += 1
    },
    parseContext(val) {
      if (val == null || val === '') {
        return null
      }
      if (typeof val === 'object') {
        return val
      }
      if (typeof val === 'string') {
        const trimmed = val.trim()
        if (!trimmed || trimmed === '{}' || trimmed === '[]') {
          return null
        }
        try {
          return JSON.parse(trimmed)
        } catch (e) {
          return trimmed
        }
      }
      return val
    },
    hasContext(val) {
      const parsed = this.parseContext(val)
      if (parsed == null) {
        return false
      }
      if (typeof parsed === 'object') {
        return Object.keys(parsed).length > 0
      }
      return String(parsed).length > 0
    },
    formatContextLabel(key) {
      if (key === 'document_type' && this.$te('docreq_document_type')) {
        return this.$t('docreq_document_type')
      }
      if (this.$te(key)) {
        return this.$t(key)
      }
      return key.replace(/_/g, ' ')
    },
    translateDocumentTypeValue(value) {
      const key = String(value)
      const documentTypeKeys = {
        framework_agreement: 'docreq_framework_role_based',
        risk_acknowledgement: 'docreq_risk_investors',
        investor_profile_agreement: 'docreq_investor_profile_investors',
        form_a: 'docreq_form_a',
        form_k: 'docreq_form_k',
        passport_front: 'passport_front',
        passport_back: 'passport_back',
        utility_bill: 'utility_bill',
        selfie: 'selfie',
      }
      const i18nKey = documentTypeKeys[key]
      if (i18nKey && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }
      const documentBasenameKeys = {
        deed_of_assignment: 'form_type_deed_of_assignment',
        non_recourse_assignment_agreement: 'form_type_non_recourse_assignment_agreement',
      }
      const basenameKey = documentBasenameKeys[key]
      if (basenameKey && this.$te(basenameKey)) {
        return this.$t(basenameKey)
      }
      if (this.$te(`form_type_${key}`)) {
        return this.$t(`form_type_${key}`)
      }
      return key
    },
    translateFormTypeValue(value) {
      const key = String(value)
      if (this.$te(`form_type_${key}`)) {
        return this.$t(`form_type_${key}`)
      }
      return this.translateDocumentTypeValue(value)
    },
    formatContextValue(value, fieldKey = null) {
      if (value == null || value === '') {
        return '—'
      }
      if (typeof value === 'boolean') {
        return value ? this.$t('yes') : this.$t('no')
      }
      if (typeof value === 'object') {
        return JSON.stringify(value)
      }
      if (fieldKey === 'document_type' || fieldKey === 'document') {
        return this.translateDocumentTypeValue(value)
      }
      if (fieldKey === 'form_type') {
        return this.translateFormTypeValue(value)
      }
      return String(value)
    },
    buildContextLines(parsed) {
      const lines = []
      const handled = new Set()

      Object.keys(parsed).forEach((key) => {
        if (handled.has(key)) {
          return
        }

        if (key.endsWith('_old')) {
          const base = key.slice(0, -4)
          const newKey = `${base}_new`
          if (Object.prototype.hasOwnProperty.call(parsed, newKey)) {
            handled.add(key)
            handled.add(newKey)
            lines.push({
              label: this.formatContextLabel(base),
              text: `${this.formatContextValue(parsed[key], base)} → ${this.formatContextValue(parsed[newKey], base)}`,
            })
            return
          }
        }

        if (key.endsWith('_new')) {
          const base = key.slice(0, -4)
          if (Object.prototype.hasOwnProperty.call(parsed, `${base}_old`)) {
            return
          }
        }

        lines.push({
          label: this.formatContextLabel(key),
          text: this.formatContextValue(parsed[key], key),
        })
      })

      return lines
    },
    formatContextDisplay(val) {
      const parsed = this.parseContext(val)
      if (parsed == null) {
        return '—'
      }
      if (typeof parsed !== 'object') {
        const text = String(parsed)
        return text.length > 120 ? `${text.slice(0, 117)}…` : text
      }
      const text = this.buildContextLines(parsed)
        .map(({ label, text: lineText }) => `${label}: ${lineText}`)
        .join(' · ')
      return text.length > 120 ? `${text.slice(0, 117)}…` : text
    },
    formatContextTitle(val) {
      const parsed = this.parseContext(val)
      if (parsed == null) {
        return ''
      }
      if (typeof parsed !== 'object') {
        return String(parsed)
      }
      return this.buildContextLines(parsed)
        .map(({ label, text }) => `${label}: ${text}`)
        .join('\n')
    },
  },
}
</script>

<style scoped>
.action-logs-filters__actions {
  gap: 0.25rem;
}
</style>
